import { create } from 'zustand';

import { deviceStorage } from '@/shared/services/storage/device-storage';

/**
 * Le thème de l'interface : clair, sombre, ou celui du système.
 *
 * Le mobile n'a qu'un thème clair ; le web en a deux, comme les réseaux que
 * les gens utilisent à côté (Instagram, TikTok). Les jetons de couleur sont
 * les mêmes rôles dans les deux thèmes : un écran n'écrit jamais « sombre ».
 * Le choix est posé avant le premier rendu par le script de `index.html` ;
 * ce store le relit, le change et suit le système quand on le lui demande.
 */
export type ThemePreference = 'system' | 'light' | 'dark';

const THEME_KEY = 'bul.theme';
const media = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function resolve(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') {
    return media()?.matches ? 'dark' : 'light';
  }
  return preference;
}

function apply(preference: ThemePreference): 'light' | 'dark' {
  const theme = resolve(preference);
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = theme;
    const meta = document.querySelector('meta[name="theme-color"]:not([media])');
    if (meta) {
      meta.setAttribute('content', theme === 'dark' ? '#0b0e17' : '#ffffff');
    }
  }
  return theme;
}

function readPreference(): ThemePreference {
  const stored = deviceStorage.readNow(THEME_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

interface ThemeState {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (preference: ThemePreference) => void;
}

const initial = readPreference();

export const useThemeStore = create<ThemeState>((set) => ({
  preference: initial,
  resolved: resolve(initial),
  setPreference(preference) {
    void deviceStorage.write(THEME_KEY, preference);
    set({ preference, resolved: apply(preference) });
  },
}));

/** Suit le système quand la préférence est « Automatique ». À appeler une fois. */
export function watchSystemTheme(): () => void {
  const list = media();
  if (!list) {
    return () => undefined;
  }
  const onChange = () => {
    const { preference } = useThemeStore.getState();
    if (preference === 'system') {
      useThemeStore.setState({ resolved: apply('system') });
    }
  };
  list.addEventListener('change', onChange);
  apply(useThemeStore.getState().preference);
  return () => list.removeEventListener('change', onChange);
}

export const useThemePreference = (): ThemePreference => useThemeStore((state) => state.preference);
export const useResolvedTheme = (): 'light' | 'dark' => useThemeStore((state) => state.resolved);
